import { Apps } from "@memorang/constants/apps";
import { getReportSkills } from "@memorang/helpers/report";
import type { BaseItemSchema, MixedItemTag } from "@memorang/types";
import type { ReactNode } from "react";
import { Divider } from "react-native-paper";
import { useAppConfig } from "../../../contexts/AppConfigContext";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import {
	type SessionReportProficiencyEstimateView,
	SessionReportProficiencySection,
} from "./SessionReportProficiencySection";
import { SessionReportScoreSection } from "./SessionReportScoreSection";
import { SessionReportStatsRow } from "./SessionReportStatsRow";
import type { OverallPoints } from "./stats-row";

export type { SessionReportProficiencyEstimateView };

export type SessionReportSummaryProps = {
	isSummativeTest: boolean;
	tags: MixedItemTag[];
	topics: string[];
	sessionItems: BaseItemSchema[];
	timeElapsed: number;
	numItems: number;
	overallScore?: {
		score: number;
		maxScore: number;
	};
	proficiencyEstimates?: SessionReportProficiencyEstimateView[];
	isLoading?: boolean;
	nextActionSlot?: ReactNode;
	sessionTipSlot?: ReactNode;
	overallPoints?: OverallPoints;
};

export const SessionReportSummary = ({
	isSummativeTest,
	tags,
	topics,
	sessionItems,
	timeElapsed,
	numItems,
	overallScore,
	proficiencyEstimates,
	isLoading = false,
	nextActionSlot,
	sessionTipSlot,
	overallPoints,
}: SessionReportSummaryProps) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const { buildType } = useAppConfig();

	const skillTags = getReportSkills(tags, topics);
	const showProficiency = Boolean(proficiencyEstimates?.length);
	const showPteOverallScoreCard =
		buildType === Apps.pte && isSummativeTest && Boolean(overallScore);
	const isSingleScore =
		showPteOverallScoreCard ||
		Boolean(overallScore) ||
		skillTags.length === 1 ||
		proficiencyEstimates?.length === 1;
	const dividerColor = theme.colors.surfaceVariant;
	const renderSlottedSection = (slot?: ReactNode) =>
		slot ? (
			<>
				<Divider style={{ backgroundColor: dividerColor }} />
				{slot}
			</>
		) : null;

	return (
		<Box
			width="100%"
			backgroundColor={theme.colors.surface}
			borderRadius={isMobile ? 0 : theme.roundness * 3}
			marginTop={isSingleScore ? 48 : 0}
			overflow="visible"
		>
			<Box
				gap={24}
				paddingHorizontal={16}
				paddingTop={24}
				paddingBottom={16}
				overflow="visible"
			>
				{showProficiency && proficiencyEstimates ? (
					<SessionReportProficiencySection estimates={proficiencyEstimates} />
				) : (
					<SessionReportScoreSection
						tags={tags}
						topics={topics}
						numItems={numItems}
						timeElapsed={timeElapsed}
						overallScore={overallScore}
						showPteOverallScoreCard={showPteOverallScoreCard}
					/>
				)}
				<SessionReportStatsRow
					isSummativeTest={isSummativeTest}
					tags={tags}
					topics={topics}
					sessionItems={sessionItems}
					timeElapsed={timeElapsed}
					isLoading={isLoading}
					showCorrectStat={showProficiency}
					overallPoints={overallPoints}
				/>
			</Box>
			{renderSlottedSection(nextActionSlot)}
			{renderSlottedSection(sessionTipSlot)}
		</Box>
	);
};
